import type { CompletionData, UpcomingTickets } from '@cobliteam/api-lib/src/__generated__/graphql';
import {
  type CompletionBeaconReason,
  type CompletionBeaconVariant,
  computeUpcomingFleetSize,
  getCompletionBeaconVariant,
  getCompletionGoalPct,
  useGoalReachedAt,
} from '@cobliteam/ui-business/components';
import useCompletionData from '../useCompletionData';
import { useGetDevices } from '../useDevices';
import useUpcomingTickets from '../useUpcomingTickets';

type TicketLike = NonNullable<NonNullable<UpcomingTickets['tickets']>[number]>;

const toDate = (value: unknown): Date | null => {
  if (value == null) return null;
  const time = typeof value === 'number' ? value : new Date(String(value)).getTime();
  if (Number.isNaN(time)) return null;
  return new Date(time);
};

const earliestDate = (dates: (Date | null)[]): Date | null => {
  const valid = dates.filter((d): d is Date => d != null);
  if (!valid.length) return null;
  return valid.reduce((earliest, current) => (current.getTime() < earliest.getTime() ? current : earliest));
};

const deriveDatesFromTickets = (tickets: (TicketLike | null | undefined)[] | null | undefined, vehicleInstalledCount: number, now: Date) => {
  const safe = (tickets ?? []).filter((t): t is TicketLike => t != null);

  const shippingTickets = safe.filter((t) => t.type === 'Shipping' || t.type === 'SHIPPING');
  const schedulingTickets = safe.filter((t) => t.type === 'Scheduling' || t.type === 'SCHEDULING');
  const returnTickets = safe.filter((t) => t.type === 'Return' || t.type === 'RETURN');

  const explicitShippingDate = earliestDate(shippingTickets.map((t) => toDate((t as { shippingDate?: string | number | null }).shippingDate)));

  const hasPostShippingEvidence = vehicleInstalledCount > 0 || returnTickets.length > 0;
  const shippingDate = explicitShippingDate ?? (hasPostShippingEvidence ? now : null);

  const deliveredTimeOfArrival = earliestDate(
    shippingTickets
      .map((t) => toDate((t as { estimatedTimeOfArrival?: string | number | null }).estimatedTimeOfArrival))
      .filter((d): d is Date => d != null && d.getTime() <= now.getTime())
  );

  const installationScheduledAt = earliestDate(schedulingTickets.map((t) => toDate((t as { scheduledDate?: string | number | null }).scheduledDate)));

  return { shippingDate, deliveredTimeOfArrival, installationScheduledAt };
};

type UseCompletionBeaconDataInput = {
  fleetId: string;
  /** Skip all three queries — e.g. a consumer whose gate (permission) is cheaper than the fetch. */
  enabled?: boolean;
  /** Override the client default (`true`), for a passive consumer that should not drive refetches. */
  refetchOnWindowFocus?: boolean;
};

type UseCompletionBeaconDataResult = {
  isLoading: boolean;
  variant: CompletionBeaconVariant | null;
  reason: CompletionBeaconReason | null;
  vehicleInstalledCount: number;
  fleetSize: number;
  upcomingFleetSize: number | undefined;
  unassociatedCount: number;
  spareUnassociatedCount: number;
  goalPct: number;
  isWithinGoalWindowDays: (days: number) => boolean;
  completionData: CompletionData | null | undefined;
  upcomingTickets: UpcomingTickets | null | undefined;
};

export const useCompletionBeaconData = ({ fleetId, enabled = true, refetchOnWindowFocus }: UseCompletionBeaconDataInput): UseCompletionBeaconDataResult => {
  /*
   * These three queries define no options of their own, so they inherit the client defaults
   * (`staleTime` 5min, `refetchOnWindowFocus: true`). That was fine while the only consumer was the
   * home beacon; a consumer mounted in the app shell needs to be able to opt out, or it pays three
   * queries on every page plus a refetch on every window focus, fleet-wide.
   */
  const options = { enabled, ...(refetchOnWindowFocus === undefined ? {} : { refetchOnWindowFocus }) };

  const { data: completionData, isLoading: isLoadingCompletion } = useCompletionData({ options });
  const { data: upcomingTickets, isLoading: isLoadingUpcoming } = useUpcomingTickets({ options });
  const { data: devices } = useGetDevices({ options });

  const vehicleInstalledCount = completionData?.vehicleInstalledCount ?? 0;
  const fleetSize = completionData?.fleetSize ?? 0;
  const goalPct = getCompletionGoalPct({ fleetSize });
  const upcomingFleetSize = computeUpcomingFleetSize({ currentFleetSize: fleetSize, upcomingTickets });

  // Disassociated devices marked as spare are set aside: they don't count toward the installation
  // goal, so they're excluded from the fleet size used for the percentage and variant. Associated
  // spare devices already count as installed (vehicleInstalledCount), so they need no adjustment.
  const spareUnassociatedCount = (devices ?? []).filter((device) => !device.vehicle && device.isSpare).length;
  const effectiveFleetSize = Math.max(0, fleetSize - spareUnassociatedCount);

  const now = new Date();
  const { shippingDate, deliveredTimeOfArrival, installationScheduledAt } = deriveDatesFromTickets(upcomingTickets?.tickets, vehicleInstalledCount, now);

  const status = getCompletionBeaconVariant({
    vehicleInstalledCount,
    fleetSize: effectiveFleetSize,
    upcomingFleetSize,
    goalPct,
    shippingDate,
    deliveredTimeOfArrival,
    installationScheduledAt,
    daysWithoutMovement: upcomingTickets?.daysWithoutMovement ?? null,
    now,
  });

  const installedPct = effectiveFleetSize > 0 ? (vehicleInstalledCount / effectiveFleetSize) * 100 : 0;
  const { isWithinGoalWindowDays } = useGoalReachedAt(fleetId, { installedPct, goalPct });

  // Devices not yet associated to a vehicle, derived against the total shown in the
  // "Total de dispositivos" card (upcomingFleetSize when it differs, else fleetSize) so
  // the three beacon cards stay arithmetically consistent.
  const unassociatedCount = Math.max(0, (upcomingFleetSize ?? fleetSize) - vehicleInstalledCount);

  return {
    // The devices query only refines spareUnassociatedCount, so it must not block the beacon paint:
    // the beacon renders from completionData/upcomingTickets and the spare adjustment applies once devices load.
    isLoading: isLoadingCompletion || isLoadingUpcoming,
    variant: status?.variant ?? null,
    reason: status?.reason ?? null,
    vehicleInstalledCount,
    fleetSize,
    upcomingFleetSize,
    unassociatedCount,
    spareUnassociatedCount,
    goalPct,
    isWithinGoalWindowDays,
    completionData,
    upcomingTickets,
  };
};

export default useCompletionBeaconData;
